<template>
	<div class="cog-input" :class="{'is-read-only': readonly}">
		<template v-if="readonly">{{ textValue || "&nbsp;" }}</template>
		<template v-else>
			<c-picker-native v-if="chameleon || mobile" :placeholder="placeholder" :show-placeholder="!chameleon && !nativeTextValue">
				<input
					:id="'cog-'+id"
					ref="input"
					v-bind="$attrs"
					:value="nativeTextValue"
					type="time"
					class="el-input__inner"
					@change="handleNativeChange"
				/>
				<template v-if="mobile" #icon>
					<i-clock :class="{'cog-offscreen': dateTimeIconIsHidden}" />
				</template>
			</c-picker-native>
			<c-select
				v-else
				:id="`${id}`"
				ref="select"
				:value="value"
				:options="options"
				:scope="scope"
				:placeholder="placeholder"
				:allow-arrows-to-change-value="allowArrowsToChangeValue"
				additional-popper-class="cog-time-popper"
				allow-other
				:filterable="true"
				filter-match="beginning"
				:editable="true"
				@change="handleChange"
				@visible-change="handleSelectVisibleChange"
			>
				<template #suffix>
					<i-clock />
				</template>
			</c-select>
		</template>
	</div>
</template>

<script>
	import CSelect from './Select.vue';
	import CPickerNative from 'src/components/PickerNative.vue';
	import IClock from './../assets/clock.svg';
	import debounce from 'src/util/debounce';
	import shouldHideDateTimeIcon from 'src/util/shouldHideDateTimeIcon';

	export default {
		name: 'CTime',
		components: {
			CSelect,
			IClock,
			CPickerNative
		},
		inject: ['$culture', '$format', '$parse', '$expandDateFormat'],
		inheritAttrs: false,
		model: {
			prop: 'value',
			event: 'change'
		},
		props: {
			value: { type: String, default: null },
			format: { type: String, default: 't' },
			placeholder: { type: String, default: null },
			allowArrowsToChangeValue: { type: Boolean, default: true },
			start: { type: String, default: null },
			end: { type: String, default: null },
			readonly: Boolean,
			chameleon: Boolean,
			mobile: Boolean,
			scope: {
				type: String,
				default: '0'
			},
			id: {
				type: String,
				default: null
			}
		},
		data: function () {
			const dateValue = this.value ? this.$parse(Date, this.value, this.$expandDateFormat(this.format)) : null;
			const textValue = this.value;
			const unparsedValue = dateValue ? null : textValue;
			const nativeTextValue = '';
			const scrollTimeOptions = debounce(startTime => {
				const cSelect = this.$refs.select;
				const elSelect = cSelect.$refs.select;
				const elOption = elSelect.findOption(startTime);
				if (elOption)
					elSelect.scrollToOptionAtTop(elOption);
			});
			const dateTimeIconIsHidden = false;
			return { dateValue, textValue, unparsedValue, nativeTextValue, scrollTimeOptions, dateTimeIconIsHidden, isFocused: null };
		},
		computed: {
			options: function() {
				const opts = [];
				// Use zero date, midnight 1/1/1970, as date baseline
				const dateBaseline = +(new Date(1970, 0, 1));
				// Can't add 24 hours to the date
				const offsetBoundary = 24 * 60 * 60 * 1000;
				// Step by 15 minutes
				const step = 15 * 60 * 1000;
				// Starting point is midnight, i.e. 12:00 AM
				let offsetStart = 0;
				if (this.start) {
					const startTime = this.$parse(Date, '1970-01-01 ' + this.start, 'yyyy-MM-dd H:mm');
					if (startTime) {
						const startTimeOffset = startTime - dateBaseline;
						if (startTimeOffset >= 0 && startTimeOffset < offsetBoundary) {
							offsetStart = startTimeOffset;
						}
					}
				}
				// Ending point is midnight minus step, i.e. 11:45 PM
				let offsetEnd = offsetBoundary - step;
				if (this.end) {
					const endTime = this.$parse(Date, '1970-01-01 ' + this.end, 'yyyy-MM-dd H:mm');
					if (endTime) {
						const endTimeOffset = endTime - dateBaseline;
						if (endTimeOffset >= 0 && endTimeOffset < offsetBoundary) {
							offsetEnd = endTimeOffset;
						}
					}
				}
				let offset = offsetStart;
				while (offset < offsetBoundary && offset <= offsetEnd) {
					const time = new Date(dateBaseline + offset);
					const formattedTime = this.$format(time, 't');
					opts.push({ displayValue: formattedTime, value: formattedTime });
					// If the first time is not a multiple of 15 minutes, then the next one should be the closest multiple of 15 minutes
					if (offset === offsetStart && (offsetStart % step) !== 0) {
						offset = (offset - (offsetStart % step)) + step;
					}
					// If the last time is not a multiple of 15 minutes, then make sure it gets included
					else if (offset !== offsetEnd && (offset + step) > offsetEnd) {
						offset = offsetEnd;
					}
					// Otherwise, move to the next 15 minute increment
					else {
						offset += step;
					}
				}
				return opts;
			},
			modelFormat: function() {
				return this.$expandDateFormat(this.format);
			}
		},
		watch: {
			value: function (newValue) {
				this.dateValue = newValue ? this.$parse(Date, newValue, this.modelFormat) : null;
				this.textValue = newValue || '';
				this.unparsedValue = this.dateValue ? null : newValue;
			},
			dateValue: function (dateValue) {
				this.nativeTextValue = dateValue ? this.$format(dateValue, 'HH:mm') : '';
			}
		},
		created: function() {
			this.nativeTextValue = this.dateValue ? this.$format(this.dateValue, 'HH:mm') : '';
		},
		beforeDestroy() {
			window.removeEventListener('resize', this.handleResize);
		},
		mounted() {
			this.dateTimeIconIsHidden = shouldHideDateTimeIcon(this.mobile, this.nativeTextValue, this.$refs.input);
			window.addEventListener('resize', this.handleResize);
		},
		methods: {
			preprocessValue(value, unparsedValue) {
				// If the format uses AM/PM and it is only numbers (ex: "9:00", "5", etc.) then append the culture's AM designator
				if (!unparsedValue && typeof value === 'string' && value.length > 0) {
					const formatHasMeridiem = this.modelFormat ? this.modelFormat.indexOf('t') !== -1 : false;
					if (formatHasMeridiem && /^\s*\d\d?(:\d\d)?\s*$/.test(value)) {
						if (!value.includes(':'))
							value = value + ':00';
						value = value + ' ' + this.$culture.dateTimeFormat.AMDesignator;
					}
				}
				return value;
			},
			handleChange(value, unparsedValue) {
				value = this.preprocessValue(value, unparsedValue);
				const textValue = unparsedValue || (typeof value === 'string' ? value : value instanceof Date ? this.$format(value, this.modelFormat) : '');
				const dateValue = unparsedValue ? null : typeof value === 'string' ? this.$parse(Date, value, this.modelFormat) : value || null;
				this.textValue = textValue;
				this.dateValue = dateValue;
				if (unparsedValue) {
					this.$emit('change', unparsedValue);
				}
				else if (value) {
					this.$emit('change', textValue);
				}
				else {
					this.$emit('change', null);
				}
			},
			handleNativeChange(ev) {
				const dateValue = ev.target.value ? this.$parse(Date, ev.target.value, 'HH:mm') : null;
				const textValue = dateValue ? this.$format(dateValue, this.modelFormat) : null;

				this.nativeTextValue = ev.target.value;
				this.textValue = textValue;
				this.dateValue = dateValue;
				this.$emit('change', textValue);
				this.dateTimeIconIsHidden = shouldHideDateTimeIcon(this.mobile, this.nativeTextValue, this.$refs.input);
			},
			handleSelectVisibleChange(visible) {
				if (visible && !this.value && !this.start) {
					const startTime = this.$parse(Date, '08:00', 'H:mm');
					const startOptionText = this.$format(startTime, 't');
					this.scrollTimeOptions(startOptionText);
				}
			},
			handleResize() {
				this.dateTimeIconIsHidden = shouldHideDateTimeIcon(this.mobile, this.nativeTextValue, this.$refs.input);
			}
		}
	};
</script>

<style lang="scss">
#{$specificity-base} {

	.cog-cognito input::-webkit-date-and-time-value {
		text-align: start;
	}

	.cog-date--time .cog-i-clock.cog-i-clock g {
		stroke-width: calc(var(--icon-weight) * 1.2px);
	}

	// Delay showing options to prevent flash of options
	// starting at 12:00AM when scrollToOptionAtTop runs.
	.cog-time-popper li {
		animation: cog-fade-in .4 .6s;
	}
}
</style>
